/**
 * @fileoverview The print-job lifecycle (`print_jobs.status`) — its vocabulary, its status machine and
 * its badge tones, declared once (LEAVE-CANON-001 R15, owner ruling on Q-R14-24).
 *
 * ## Why it lives here
 *
 * A print job's status was spelled three times: pos-api's `constants/printing` (the vocabulary), the
 * `PrintingJob` model (the machine, hoisted out of `updateStatus` by LIFECYCLE-UPDATE-RULE-001) and
 * pos-web's `JOB_STATUSES` + `JOB_STATUS_TO_BADGE` (a hand mirror). The printing unit gives the screen
 * status MOVES, and a screen can only offer the moves the server will accept if it reads the same
 * machine — so it lives here, beside the shipment and mail-item lifecycles, and both apps import it.
 *
 * ## The states
 *
 * - `queued` — created and waiting for a machine (every job is created here; the create contract
 *   refuses a client-chosen status).
 * - `printing` — on a machine; the move stamps `startedAt`.
 * - `completed` — printed. Terminal: a completed job moves on only by being RUNG UP, which is not a
 *   status but the job's sale link (`print_jobs.transactionId`, set once) — until then it is "Ready".
 * - `failed` — the run failed, with its reason (`errorMessage`); an operator re-queues it.
 *
 * Every status is a KEY, a terminal one mapping to `[]` — the `SHIPMENT_STATUS_TRANSITIONS`
 * convention, so a reader can list a status's targets without knowing which convention was used.
 *
 * @module @puffin-core/status-taxonomy/printJobStatus
 */

'use strict';

const { BADGE_TONE } = require('./badgeTones');

/**
 * The print-job lifecycle, in lifecycle order.
 *
 * @type {Readonly<Object<string, string>>}
 */
const PRINT_JOB_STATUS = Object.freeze({
  QUEUED: 'queued',
  PRINTING: 'printing',
  COMPLETED: 'completed',
  FAILED: 'failed',
});

/**
 * Every status, in lifecycle order — what the list filter offers and the status CHECK admits.
 *
 * @type {ReadonlyArray<string>}
 */
const PRINT_JOB_STATUS_VALUES = Object.freeze(Object.values(PRINT_JOB_STATUS));

/**
 * Every legal transition, spelled once.
 *
 * @type {Readonly<Object<string, ReadonlyArray<string>>>}
 */
const PRINT_JOB_STATUS_TRANSITIONS = Object.freeze({
  [PRINT_JOB_STATUS.QUEUED]: Object.freeze([PRINT_JOB_STATUS.PRINTING, PRINT_JOB_STATUS.FAILED]),
  [PRINT_JOB_STATUS.PRINTING]: Object.freeze([PRINT_JOB_STATUS.COMPLETED, PRINT_JOB_STATUS.FAILED]),
  [PRINT_JOB_STATUS.COMPLETED]: Object.freeze([]),
  [PRINT_JOB_STATUS.FAILED]: Object.freeze([PRINT_JOB_STATUS.QUEUED]),
});

/** The empty answer, shared so a caller can never mutate a status's targets through it. */
const NO_TARGETS = Object.freeze([]);

/**
 * The statuses a print job at `status` may move to next — what a screen offers.
 *
 * Reads OWN keys only, so an unknown value (or an inherited `Object.prototype` name such as
 * `constructor`) answers `[]` rather than a function.
 *
 * @param {string} status - The job's current status.
 * @returns {ReadonlyArray<string>} The legal targets, in the machine's order; `[]` when terminal or
 *   unknown.
 */
function printJobStatusTargets(status) {
  return typeof status === 'string' &&
    Object.prototype.hasOwnProperty.call(PRINT_JOB_STATUS_TRANSITIONS, status)
    ? PRINT_JOB_STATUS_TRANSITIONS[status]
    : NO_TARGETS;
}

/**
 * Each status's badge TONE (`<StatusBadgeCell statusTones>`, Q-MI-25) — the tones pos-web's job list
 * already showed: waiting (`neutral`), on a machine (`info`), printed (`success`), failed (`danger`).
 *
 * @type {Readonly<Object<string, string>>}
 */
const PRINT_JOB_STATUS_BADGE_TONES = Object.freeze({
  [PRINT_JOB_STATUS.QUEUED]: BADGE_TONE.NEUTRAL,
  [PRINT_JOB_STATUS.PRINTING]: BADGE_TONE.INFO,
  [PRINT_JOB_STATUS.COMPLETED]: BADGE_TONE.SUCCESS,
  [PRINT_JOB_STATUS.FAILED]: BADGE_TONE.DANGER,
});

module.exports = {
  PRINT_JOB_STATUS,
  PRINT_JOB_STATUS_VALUES,
  PRINT_JOB_STATUS_TRANSITIONS,
  PRINT_JOB_STATUS_BADGE_TONES,
  printJobStatusTargets,
};
